/**
 * Design 1: the task feed (left) + driver Active Context drill-down (right).
 *
 * The feed is the primary interface — a filterable, one-click to-do list that
 * keeps the dispatcher in flow. Selecting a task drives the drill-down so the
 * recommendation can be validated without navigating away.
 *
 * Data is injected via props so the same view backs both the standalone
 * prototype (static demo data) and the in-app page (real roster + demo overlay).
 */

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { type AssignmentOption, assignmentOptionsFor } from "./assignments";
import { text, zinc } from "./colors";
import { ActiveContextPanel, type DriverEdits } from "./components/ActiveContextPanel";
import { MessageComposer } from "./components/MessageComposer";
import { Chip, Pill } from "./components/primitives";
import { TaskAssignmentDialog } from "./components/TaskAssignmentDialog";
import { TaskCard } from "./components/TaskCard";
import type { DispatchTask, Driver, TaskState } from "./types";

/** Overlay dispatcher edits (status, projected times) onto a driver for display. */
/** Swap the trailing time token of a "<label…> <time>" target string, keeping the label. */
function replaceTargetTime(target: string | undefined, time: string | undefined): string | undefined {
  if (time === undefined) return target;
  const label = target?.split(" ").slice(0, -1).join(" ");
  return label ? `${label} ${time}` : time;
}

function applyDriverEdits(driver: Driver, edits?: DriverEdits): Driver {
  if (!edits) return driver;
  return {
    ...driver,
    status: edits.status,
    statusLabel: edits.statusLabel,
    appointmentRisk: driver.appointmentRisk
      ? {
          ...driver.appointmentRisk,
          projected: edits.apptProjected ?? driver.appointmentRisk.projected,
        }
      : driver.appointmentRisk,
    pta: driver.pta
      ? {
          ...driver.pta,
          time: edits.ptaProjected ?? driver.pta.time,
          risk: {
            ...driver.pta.risk,
            target: replaceTargetTime(driver.pta.risk.target, edits.ptaTarget),
            projected: edits.ptaProjected ?? driver.pta.risk.projected,
          },
        }
      : driver.pta,
  };
}

type FilterKey = "all" | "assigned" | "urgent" | "pta" | "empty" | "breakdown" | "awaiting";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned to me" },
  { key: "urgent", label: "Urgent" },
  { key: "pta", label: "PTA at risk" },
  { key: "empty", label: "Empty call" },
  { key: "breakdown", label: "Breakdown" },
  { key: "awaiting", label: "Awaiting response" },
];

function matchesFilter(task: DispatchTask, filter: FilterKey): boolean {
  switch (filter) {
    case "assigned":
      return task.assignedToMe === true;
    case "urgent":
      return task.urgency === "urgent";
    case "pta":
      return task.type === "pta_risk";
    case "empty":
      return task.type === "empty_call" || task.type === "empty_call_failed";
    case "breakdown":
      return task.type === "breakdown";
    case "awaiting":
      return task.state === "awaiting_response";
    default:
      return true;
  }
}

interface TaskFeedViewProps {
  drivers: Driver[];
  tasks: DispatchTask[];
  onToast: (message: string) => void;
  /**
   * Real send path (in-app). When provided, "Send message" calls this with the
   * driver + message text. When omitted (standalone), the send is simulated.
   */
  onSendMessage?: (driver: Driver, text: string) => void | Promise<void>;
  /** Optional control rendered at the right of the feed header (e.g. "Seed data"). */
  headerAction?: ReactNode;
  /** When this changes, select the matching driver (used to focus newly seeded tasks). */
  focusDriverId?: string;
  /** Opens the driver's message + workflow history (the feed) from the active context. */
  onOpenMessageHistory?: (driver: Driver) => void;
  /**
   * Controlled task-resolution overrides (task id → state). When provided, the
   * shell owns resolution state so other views (e.g. the live feed's board
   * counts) react to it; otherwise the feed keeps its own local state.
   */
  resolved?: Record<string, TaskState>;
  onResolveTask?: (taskId: string, nextState: TaskState) => void;
}

export function TaskFeedView({
  drivers,
  tasks: incomingTasks,
  onToast,
  onSendMessage,
  headerAction,
  focusDriverId,
  onOpenMessageHistory,
  resolved: controlledResolved,
  onResolveTask,
}: TaskFeedViewProps) {
  const [filter, setFilter] = useState<FilterKey>("all");
  // Local resolutions overlaid by task id, so the `tasks`/`drivers` props can update
  // (e.g. when the real roster loads) without losing dispatcher actions or stale-binding cards.
  // When the shell passes controlled state, defer to it instead.
  const [localResolved, setLocalResolved] = useState<Record<string, TaskState>>({});
  const resolved = controlledResolved ?? localResolved;
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [composer, setComposer] = useState<{ task: DispatchTask; driver: Driver } | null>(null);
  const [assignDriver, setAssignDriver] = useState<Driver | null>(null);
  const [sending, setSending] = useState(false);
  // Dispatcher edits (status / projected times) overlaid by driver id, pushed to AS400 on save.
  const [driverEdits, setDriverEdits] = useState<Record<string, DriverEdits>>({});

  const driverById = useMemo(() => new Map(drivers.map((d) => [d.id, d])), [drivers]);
  const tasks = useMemo(
    () => incomingTasks.map((t) => (resolved[t.id] ? { ...t, state: resolved[t.id] } : t)),
    [incomingTasks, resolved],
  );
  const visibleTasks = useMemo(() => tasks.filter((t) => matchesFilter(t, filter)), [tasks, filter]);
  const openCount = tasks.filter(
    (t) => t.state === "suggested" || t.state === "requires_attention" || t.state === "awaiting_response",
  ).length;

  // Keep the selection valid as data loads: fall back to the first visible task's driver.
  const effectiveSelectedId =
    selectedDriverId && driverById.has(selectedDriverId)
      ? selectedDriverId
      : (visibleTasks[0]?.driverId ?? drivers[0]?.id);
  const baseSelectedDriver = effectiveSelectedId ? driverById.get(effectiveSelectedId) : undefined;
  // Prefer an empty-call-failed task so its override surfaces in Active Context.
  const selectedTask = effectiveSelectedId
    ? (tasks.find((t) => t.driverId === effectiveSelectedId && t.emptyCallFailure) ??
      tasks.find((t) => t.driverId === effectiveSelectedId))
    : undefined;
  const selectedDriver = baseSelectedDriver
    ? {
        ...applyDriverEdits(baseSelectedDriver, driverEdits[baseSelectedDriver.id]),
        emptyCallFailure: selectedTask?.emptyCallFailure ?? baseSelectedDriver.emptyCallFailure,
      }
    : undefined;

  useEffect(() => {
    if (focusDriverId) setSelectedDriverId(focusDriverId);
  }, [focusDriverId]);

  // Keyboard shortcuts mirroring the live feed: A assign, O message history, R reply.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      if (
        el instanceof HTMLElement &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)
      ) {
        return;
      }
      if (!selectedDriver || composer || assignDriver) return;
      const key = e.key.toLowerCase();
      if (key === "a") {
        e.preventDefault();
        setAssignDriver(selectedDriver);
      } else if (key === "o" && onOpenMessageHistory) {
        e.preventDefault();
        onOpenMessageHistory(selectedDriver);
      } else if (key === "r") {
        e.preventDefault();
        const task = tasks.find((t) => t.driverId === selectedDriver.id);
        if (task) setComposer({ task, driver: selectedDriver });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedDriver, composer, assignDriver, onOpenMessageHistory, tasks]);

  const resolveTask = (taskId: string, nextState: TaskState, message: string) => {
    if (onResolveTask) onResolveTask(taskId, nextState);
    else setLocalResolved((prev) => ({ ...prev, [taskId]: nextState }));
    onToast(message);
  };

  const submitAssign = (option: AssignmentOption) => {
    if (assignDriver) {
      const task = tasks.find((t) => t.driverId === assignDriver.id);
      if (task) resolveTask(task.id, "done", `Assigned to ${option.name}`);
      else onToast(`Assigned to ${option.name}`);
    }
    setAssignDriver(null);
  };

  const handleConfirmSend = async (messageText: string, resolve: boolean) => {
    if (!composer) return;
    const { task, driver } = composer;
    setSending(true);
    try {
      await onSendMessage?.(driver, messageText);
      resolveTask(
        task.id,
        resolve ? "done" : "awaiting_response",
        resolve ? `Message sent to ${driver.name} · resolved` : `Message sent to ${driver.name} · awaiting response`,
      );
      setComposer(null);
    } catch {
      onToast(`Couldn't send message to ${driver.name}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
      {/* left: task feed */}
      <Box
        sx={{
          width: 420,
          flexShrink: 0,
          borderRight: `1px solid ${zinc[800]}`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.75 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: text.primary }}>Tasks</Typography>
            <Chip label={`${openCount} open`} tone="neutral" />
            <Box sx={{ flex: 1 }} />
            {headerAction}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {FILTERS.map((f) => (
              <Pill key={f.key} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            pb: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            minHeight: 0,
          }}
        >
          {visibleTasks.map((task) => {
            const driver = driverById.get(task.driverId);
            if (!driver) return null;
            return (
              <TaskCard
                key={task.id}
                task={task}
                driver={driver}
                selected={effectiveSelectedId === task.driverId}
                onSelect={() => setSelectedDriverId(task.driverId)}
                onSend={() => setComposer({ task, driver })}
                onMarkResolved={() => resolveTask(task.id, "done", `Task resolved for ${driver.name}`)}
              />
            );
          })}
          {visibleTasks.length === 0 && (
            <Typography sx={{ fontSize: 13, color: text.muted, textAlign: "center", mt: 4 }}>
              No tasks match this filter.
            </Typography>
          )}
        </Box>
      </Box>

      {/* right: active context */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selectedDriver ? (
          <ActiveContextPanel
            driver={selectedDriver}
            onSave={(driverId, edits) => {
              setDriverEdits((prev) => ({ ...prev, [driverId]: edits }));
              onToast(`Saved ${selectedDriver.name} · pushed to AS400`);
            }}
            onOpenMessageHistory={onOpenMessageHistory ? () => onOpenMessageHistory(selectedDriver) : undefined}
            onAssign={() => setAssignDriver(selectedDriver)}
          />
        ) : (
          <Box sx={{ p: 4, color: text.muted }}>Select a task to see driver context.</Box>
        )}
      </Box>

      {composer && (
        <MessageComposer
          driverName={composer.driver.name}
          recommendation={composer.task.cannedMessage}
          sending={sending}
          onCancel={() => setComposer(null)}
          onSend={(text, resolve) => handleConfirmSend(text, resolve)}
        />
      )}

      {assignDriver && (
        <TaskAssignmentDialog
          title={`Assign ${assignDriver.name} · Truck ${assignDriver.truck}`}
          options={assignmentOptionsFor(assignDriver.id)}
          onSubmit={submitAssign}
          onCancel={() => setAssignDriver(null)}
        />
      )}
    </Box>
  );
}
